/***
 *
 *   SIGN IN
 *   Sign in form for users, admins and owners.
 *
 **********/

import { useContext, useState, useEffect } from "react";
import {
  AuthContext,
  ViewContext,
  Button,
  Form,
  Link,
  Separator,
  SocialSignin,
  useSearchParams,
  useNavigate,
  Checkbox,
  useToast,
} from "components/lib";

export function Signin({ t }) {
  const navigate = useNavigate();

  // context
  const authContext = useContext(AuthContext);

  const viewContext = useContext(ViewContext);
  const { dismiss } = useToast();

  // state
  const [useMagic, setUseMagic] = useState(false);
  const [searchParams] = useSearchParams();

  const [form, setForm] = useState({
    email: {
      label: t("auth.signin.index.form.email.label"),
      type: "email",
      required: true,
    },
    password: {
      label: t("auth.signin.index.form.password.label"),
      type: "password",
      required: true,
    },
    remember: {
      label: t("auth.signin.index.form.remember.label"),
      type: "checkbox",
    },
  });

  useEffect(() => {
    // Dismiss all existing toasts first
    dismiss();

    // was an error message passed from the server router?
    const error = searchParams.get("error");

    if (error)
      viewContext.notification({ description: error, variant: "error" });

    // Show free tier message only if not shown in last 2 hours
    const lastShown = localStorage.getItem("freeTierToastShown");
    const now = Date.now();
    const twoHours = 2 * 60 * 60 * 1000;

    if (!lastShown || now - parseInt(lastShown) > twoHours) {
      viewContext.notification({
        title: t("auth.free_tier.title") || "Free to get started!",
        description: t("auth.free_tier.description") || "Sign up for free - no credit card needed to create an account and use the free tier.",
        variant: "info",
        duration: Infinity,
      });
      localStorage.setItem("freeTierToastShown", now.toString());
    }
  }, []);

  function toggleMagicLink(useMagic) {
    const f = { ...form };
    f.password.type = useMagic ? null : "password";
    setForm(f);
    setUseMagic(useMagic);
  }

  return (
    <div className="w-full max-w-sm mx-auto space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="text-2xl sm:text-3xl font-light text-white fade-in tracking-wide">
          Sign in to MapAtlas
        </h1>
        <p className="text-white/60 text-sm font-light slide-up" style={{animationDelay: '0.1s'}}>
          Secure access to your analytics dashboard
        </p>
      </div>

      {/* Authentication Method Toggle */}
      <div className="space-y-3 scale-in" style={{animationDelay: '0.2s'}}>
        {useMagic ? (
          <Button
            text={t("auth.signin.index.button.use_password")}
            icon="shield"
            size="full"
            variant="outline"
            className="glass-input hover:glass-button border-white/30 text-white hover:text-white h-11 sm:h-12 text-sm font-light tracking-wide transition-all duration-300 hover:scale-105"
            action={() => {
              toggleMagicLink(false);
            }}
          />
        ) : (
          <Button
            text={t("auth.signin.index.button.use_magic")}
            icon="star"
            size="full"
            variant="outline"
            className="glass-input hover:glass-button border-white/30 text-white hover:text-white h-11 sm:h-12 text-sm font-light tracking-wide transition-all duration-300 hover:scale-105"
            action={() => {
              toggleMagicLink(true);
            }}
          />
        )}
      </div>

      {/* Login Form */}
      <div className="scale-in" style={{animationDelay: '0.3s'}}>
        <Form
          inputs={form}
          method="POST"
          url={useMagic ? "/api/auth/magic" : "/api/auth"}
          buttonText={
            useMagic
              ? t("auth.signin.index.form.button.use_magic")
              : t("auth.signin.index.form.button.use_password")
          }
          className="space-y-4"
          callback={(res) => {
            if (useMagic) {
              viewContext.notification({
                title: t("auth.signin.index.message.title"),
                description: t("auth.signin.index.message.description"),
              });
            } else {
              res.data["2fa_required"]
                ? navigate(`/signin/otp?token=${res.data.token}`)
                : navigate(authContext.signin(res));
            }
          }}
        />
      </div>

      {/* Forgot Password Link */}
      <div className="flex justify-center slide-up" style={{animationDelay: '0.4s'}}>
        <Link
          url="/forgotpassword"
          text="Forgot your password?"
          className="text-white/60 hover:text-white/90 text-xs font-light tracking-wide transition-colors duration-200 hover:underline"
        />
      </div>

      {/* Footer */}
      <footer className="justify-center text-xs space-x-2 slide-up font-light" style={{animationDelay: '0.5s'}}>
        <span className="text-white/50">New to MapAtlas?</span>
        <Link 
          url="/signup" 
          text="Create account" 
          className="text-white/80 hover:text-white tracking-wide transition-colors duration-200 hover:underline"
        />
      </footer>
    </div>
  );
}
